export function createUiHelper({
  document = typeof window !== 'undefined' ? window.document : null,
  toastRootId = 'toast-root',
} = {}) {
  if (!document) {
    return null;
  }

  let toastRoot = document.getElementById(toastRootId) || null;

  const ensureToastRoot = () => {
    if (toastRoot && toastRoot.isConnected) {
      return toastRoot;
    }

    toastRoot = document.getElementById(toastRootId) || null;
    if (toastRoot && toastRoot.isConnected) {
      return toastRoot;
    }

    if (!document.body) {
      return null;
    }

    const root = document.createElement('div');
    root.id = toastRootId;
    document.body.appendChild(root);
    toastRoot = root;
    return toastRoot;
  };

  const escapeHtml = (value) =>
    String(value || '').replace(/[&<>"']/g, (match) => {
      switch (match) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        case "'":
          return '&#39;';
        default:
          return match;
      }
    });

  const uid = () => `id_${Math.random().toString(36).slice(2, 9)}${Date.now().toString(36)}`;

  const esc = (value) => escapeHtml(value);

  const ensureVisible = (element) => {
    if (!element) {
      return;
    }

    try {
      element.scrollIntoView({ behavior: 'smooth', block: 'end' });
    } catch (error) {
      // ignore scroll failures
    }

    try {
      const focusable = element.querySelector('textarea, input, [contenteditable="true"]');
      focusable?.focus?.();
    } catch (error) {
      // ignore focus failures
    }
  };

  const makeModal = (idPrefix) => {
    if (!idPrefix) {
      return null;
    }

    const modal = document.getElementById(`${idPrefix}-modal`);
    const backdrop = document.getElementById(`${idPrefix}-backdrop`);
    const closeBtn = document.getElementById(`${idPrefix}-close`);
    const saveBtn = document.getElementById(`${idPrefix}-save`);
    const cancelBtn = document.getElementById(`${idPrefix}-cancel`);

    const open = () => {
      modal?.classList?.remove?.('hidden');
    };

    const close = () => {
      modal?.classList?.add?.('hidden');
    };

    backdrop?.addEventListener?.('click', close);
    closeBtn?.addEventListener?.('click', close);
    cancelBtn?.addEventListener?.('click', close);

    return { open, close, saveBtn, modal };
  };

  const showToast = (message, type = 'info', title = null, timeout = 3000) => {
    const root = ensureToastRoot();
    if (!root) {
      return;
    }

    const node = document.createElement('div');
    node.className = `toast ${type}`;
    node.innerHTML = `
      <div>
        ${title ? `<div class="title">${title}</div>` : ''}
        <div class="msg">${message}</div>
      </div>
      <button class="close" aria-label="Close">&times;</button>
    `;

    const removeNode = () => {
      if (node.isConnected) {
        try {
          root.removeChild(node);
        } catch (error) {
          // ignore remove failures
        }
      }
    };

    node.querySelector('.close')?.addEventListener('click', removeNode);
    root.appendChild(node);

    if (timeout > 0) {
      setTimeout(removeNode, timeout);
    }
  };

  const resolveConfirmElements = () => {
    const modal = document.getElementById('confirm-modal');
    const backdrop = document.getElementById('confirm-backdrop');
    const close = document.getElementById('confirm-close');
    const ok = document.getElementById('confirm-ok');
    const cancel = document.getElementById('confirm-cancel');
    const message = document.getElementById('confirm-message');
    const details = document.getElementById('confirm-details');
    const title = document.getElementById('confirm-title');

    if (!modal || !ok || !cancel || !message || !title) {
      return null;
    }

    return { modal, backdrop, close, ok, cancel, message, details, title };
  };

  const showConfirm = (options = {}) => {
    const elements = resolveConfirmElements();
    if (!elements) {
      const defaultView = document.defaultView || null;
      if (defaultView && typeof defaultView.confirm === 'function') {
        const result = defaultView.confirm(options.message || 'Are you sure you want to proceed?');
        return Promise.resolve(Boolean(result));
      }
      return Promise.resolve(false);
    }

    const {
      modal,
      backdrop,
      close,
      ok,
      cancel,
      message,
      details,
      title,
    } = elements;

    return new Promise((resolve) => {
      const { okText = 'OK', cancelText = 'Cancel' } = options;
      title.textContent = options.title || 'Confirm Action';
      message.textContent = options.message || 'Are you sure you want to proceed?';
      details.textContent = options.details || '';
      details.style.display = options.details ? 'block' : 'none';
      ok.textContent = okText;
      cancel.textContent = cancelText;

      const cleanup = () => {
        modal.classList.add('hidden');
        ok.removeEventListener('click', handleOk);
        cancel.removeEventListener('click', handleCancel);
        close?.removeEventListener('click', handleCancel);
        backdrop?.removeEventListener('click', handleCancel);
        document.removeEventListener('keydown', handleKeydown);
      };

      const handleOk = () => {
        cleanup();
        resolve(true);
      };

      const handleCancel = () => {
        cleanup();
        resolve(false);
      };

      const handleKeydown = (event) => {
        if (event.key === 'Escape') {
          handleCancel();
        }
      };

      modal.classList.remove('hidden');
      ok.focus();

      ok.addEventListener('click', handleOk, { once: true });
      cancel.addEventListener('click', handleCancel, { once: true });
      close?.addEventListener('click', handleCancel, { once: true });
      backdrop?.addEventListener('click', handleCancel, { once: true });
      document.addEventListener('keydown', handleKeydown);
    });
  };

  return {
    makeModal,
    showToast,
    showConfirm,
    uid,
    esc,
    ensureVisible,
  };
}
